import { useLayoutEffect, useRef, useState, type PointerEvent } from 'react'
import { useStatsT } from './statsContext'
import { chartDate, chartModel, sparkPath, valueLabel, type Series, type Tone } from './statisticsData'

/** Width reserved on the right of the plot for the y labels (matches .ofx-chart-plot). */
const AXIS_W = 52

/**
 * OnlyFans' statistics chart as inline SVG: a filled main series with a
 * smaller second series underneath, y labels on the right, dates below, and a
 * crosshair + tooltip that follows the pointer.
 */
export function StatsChart({ main, sub = null }: { main: Series; sub?: Series | null }) {
  const { intl } = useStatsT()
  const plotRef = useRef<HTMLDivElement | null>(null)
  const tipRef = useRef<HTMLDivElement | null>(null)
  const [hover, setHover] = useState<{ i: number; left: number; width: number } | null>(null)
  const model = chartModel(main, sub, intl)

  useLayoutEffect(() => {
    const tip = tipRef.current
    if (!tip || !hover) return
    const w = tip.offsetWidth
    tip.style.left = `${Math.min(Math.max(hover.left - w / 2, 0), Math.max(0, hover.width - w))}px`
  }, [hover])

  if (!model) return null

  const onMove = (e: PointerEvent<HTMLDivElement>) => {
    const rect = plotRef.current?.getBoundingClientRect()
    if (!rect) return
    const width = Math.max(1, rect.width - AXIS_W)
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / width))
    const i = Math.round(frac * (model.times.length - 1))
    setHover({ i, left: ((model.xs[i] ?? 0) / model.W) * width, width })
  }

  const i = hover && hover.i < model.times.length ? hover.i : null
  return (
    <div className="ofx-chart">
      <div className="ofx-chart-plot" ref={plotRef} onPointerMove={onMove} onPointerLeave={() => setHover(null)}>
        <svg viewBox={`0 0 ${model.W} ${model.H}`} preserveAspectRatio="none" style={{ height: Math.round(model.H * 1.25) }} role="img" aria-label={main.label}>
          <g className="ofx-chart-grid">
            {model.grid.map((y) => (
              <line key={y} x1="0" x2={model.W} y1={y.toFixed(1)} y2={y.toFixed(1)} vectorEffect="non-scaling-stroke" />
            ))}
            <line x1="0" x2={model.W} y1={model.mainH} y2={model.mainH} vectorEffect="non-scaling-stroke" />
          </g>
          <path className="ofx-chart-area" d={model.area} />
          <path className="ofx-chart-line" d={model.line} vectorEffect="non-scaling-stroke" />
          {sub && (
            <>
              <path className="ofx-chart-sub-area" d={model.subArea} />
              <path className="ofx-chart-sub-line" d={model.subLine} vectorEffect="non-scaling-stroke" />
            </>
          )}
        </svg>
        <div className="ofx-chart-y" aria-hidden="true">
          {model.yLabels.map((l, k) => (
            <span key={k} style={{ top: `${l.top.toFixed(2)}%` }}>
              {l.text}
            </span>
          ))}
        </div>
        {i !== null && hover && (
          <>
            <div className="ofx-chart-cross" style={{ left: hover.left }} />
            <div className="ofx-chart-tip" ref={tipRef} role="tooltip">
              <div className="ofx-chart-tip-date">{chartDate(model, i, intl)}</div>
              <div>
                <span className="ofx-dot ofx-dot--main" aria-hidden="true" />
                {main.label}
                <strong>{valueLabel(model.mainVals[i] ?? 0, main.kind)}</strong>
              </div>
              {sub && (
                <div>
                  <span className="ofx-dot ofx-dot--sub" aria-hidden="true" />
                  {sub.label}
                  <strong>{valueLabel(model.subVals[i] ?? 0, sub.kind)}</strong>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="ofx-chart-x" aria-hidden="true">
        {model.xTicks.map((tick, k) => (
          <span key={k} className={`is-${tick.align}`} style={{ left: `${tick.left.toFixed(2)}%` }}>
            {tick.top}
            <br />
            {tick.bottom}
          </span>
        ))}
      </div>
    </div>
  )
}

/** The summary cards' small trend line (flat when there is nothing to draw). */
export function Spark({ points, tone = 'total' }: { points: unknown; tone?: Tone }) {
  const d = sparkPath(points)
  return (
    <svg className="ofx-spark" viewBox="0 0 130 40" preserveAspectRatio="none" aria-hidden="true">
      {d ? (
        <path className={`ofx-spark-line ofx-tone-${tone}`} d={d} vectorEffect="non-scaling-stroke" />
      ) : (
        <line className={`ofx-spark-line ofx-tone-${tone}`} x1="0" x2="130" y1="38" y2="38" />
      )}
    </svg>
  )
}
